// covered-by: frontends/carrie/src/ask-v2/review/__tests__/useAskV2ReviewPinView.test.ts
//
// How the review's pins are shown (warhol spec dave.savostyanov/0090): every
// pin, or the open findings in groups with one pin per group until a group is
// opened, and whether minor and check findings show at all.
import { type AskPunttReviewGroupSummary } from "@mg/schemas/src/christo/askPunttReview";
import { useCallback, useEffect, useMemo, useState } from "react";

import {
  groupedComments,
  orderPins,
  type PinGroup,
  type PinGroupBy,
} from "./pinGroups";
import { type CommentWithOptionalTags } from "./reviewCommentTags";

import { useIsFlagOn } from "../../config/featureFlags";
import {
  type LoadedReviewCommentGroups,
  reviewCommentGroup,
} from "../../utils/visibleReviewComments";

export type PinViewMode = "flat" | "groups";

const STORAGE_KEY = "ask-v2-pin-view";

type StoredPinView = {
  mode: PinViewMode;
  groupBy: PinGroupBy;
  showMinor: boolean;
};

const DEFAULT_VIEW: StoredPinView = {
  mode: "groups",
  groupBy: "block",
  showMinor: true,
};

// why try/catch: storage throws in a private window and is empty in a preview;
// the view is a convenience, so it falls back to the default.
function readStored(): StoredPinView {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_VIEW;
    const parsed = JSON.parse(raw) as Partial<StoredPinView>;
    return {
      mode: parsed.mode === "flat" ? "flat" : "groups",
      groupBy: parsed.groupBy === "place" ? "place" : "block",
      showMinor: parsed.showMinor !== false,
    };
  } catch {
    return DEFAULT_VIEW;
  }
}

function writeStored(view: StoredPinView) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(view));
  } catch {
    // A view that is not remembered is still the view on screen.
  }
}

type UseAskV2ReviewPinViewInput = {
  /** The findings the panel draws, in the order the list holds them. */
  comments: readonly CommentWithOptionalTags[];
  loadedGroups: LoadedReviewCommentGroups;
  summaries?: readonly AskPunttReviewGroupSummary[];
  /** The finding open in the list; its group opens with it. */
  selectedCommentId?: string | null;
};

export function useAskV2ReviewPinView({
  comments,
  loadedGroups,
  summaries,
  selectedCommentId = null,
}: UseAskV2ReviewPinViewInput) {
  const flagOn = useIsFlagOn("ask-v2-pin-groups");
  const [view, setView] = useState<StoredPinView>(readStored);
  const [openGroupKey, setOpenGroupKey] = useState<string | null>(null);
  const update = useCallback((patch: Partial<StoredPinView>) => {
    setView((current) => {
      const next = { ...current, ...patch };
      writeStored(next);
      return next;
    });
    setOpenGroupKey(null);
  }, []);

  const enabled = flagOn;
  const showMinor = !enabled || view.showMinor;
  const grouping = enabled && view.mode === "groups";

  const isOpenFinding = useCallback(
    (comment: CommentWithOptionalTags) =>
      (loadedGroups.get(comment._id) ?? reviewCommentGroup(comment)) ===
      "unresolved",
    [loadedGroups],
  );
  // Minor and check findings leave the list and the artwork together.
  const { companions, allGroups, groups, ordered } = useMemo(
    () =>
      orderPins(comments, {
        enabled,
        showMinor,
        grouping,
        groupBy: view.groupBy,
        summaries,
        isOpen: isOpenFinding,
      }),
    [
      comments,
      enabled,
      grouping,
      isOpenFinding,
      showMinor,
      summaries,
      view.groupBy,
    ],
  );

  const openGroup = groups.find((g) => g.key === openGroupKey) ?? null;
  // why: a group that a filter or the minor switch emptied cannot stay open.
  useEffect(() => {
    if (openGroupKey != null && openGroup == null) setOpenGroupKey(null);
  }, [openGroup, openGroupKey]);

  const groupOf = useCallback(
    (commentId: string) => {
      const shownAs = companions.leadOf.get(commentId) ?? commentId;
      return (
        groups.find((g) => g.members.some((m) => m._id === shownAs)) ?? null
      );
    },
    [companions, groups],
  );
  // why: a finding opened from the list or a pin must land in an open group,
  // or its card and its pin are both hidden behind the group.
  useEffect(() => {
    if (!grouping || selectedCommentId == null) return;
    const shownAs =
      companions.leadOf.get(selectedCommentId) ?? selectedCommentId;
    const holder = groups.find((g) => g.members.some((m) => m._id === shownAs));
    if (holder && holder.key !== openGroupKey) setOpenGroupKey(holder.key);
  }, [companions, grouping, groups, openGroupKey, selectedCommentId]);

  const hubGroup = useCallback(
    (commentId: string) => groups.find((g) => g.hub._id === commentId) ?? null,
    [groups],
  );

  /** What the artwork draws: every pin, one pin per group, or the open group's pins. */
  const viewerComments = useMemo(() => {
    if (!grouping) return ordered;
    const decided = ordered.filter((c) => !isOpenFinding(c));
    if (openGroup) return [...openGroup.members, ...decided];
    return [...groups.map((g) => g.hub), ...decided];
  }, [grouping, groups, isOpenFinding, openGroup, ordered]);

  return {
    enabled,
    grouping,
    mode: view.mode,
    groupBy: view.groupBy,
    showMinor,
    setMode: (mode: PinViewMode) => update({ mode }),
    setGroupBy: (groupBy: PinGroupBy) => update({ groupBy }),
    setShowMinor: (next: boolean) => update({ showMinor: next }),
    groups,
    groupCount: allGroups.length,
    ordered,
    viewerComments,
    openGroup,
    openGroupKey: openGroup?.key ?? null,
    setOpenGroupKey,
    groupOf,
    hubGroup,
    companionsOf: companions.companionsOf,
    /** The finding whose pin and card show `commentId`: its lead when it is a companion. */
    pinOf: (commentId: string | null) =>
      commentId == null
        ? null
        : (companions.leadOf.get(commentId) ?? commentId),
    /**
     * The finding before or after `fromId` in group order, wrapping into the
     * next group past the last pin of this one; with nothing in the groups
     * selected, the first or last of `within`, the group the bar is open on.
     *
     * why a finding and not a group: stepping only the open group fought the
     * selection, which reopens the group its finding sits in.
     */
    stepFinding: (
      direction: 1 | -1,
      fromId: string | null,
      within: PinGroup,
    ): string => {
      const list = groupedComments(groups);
      const at = fromId == null ? -1 : list.findIndex((c) => c._id === fromId);
      if (at >= 0) {
        return list[(at + direction + list.length) % list.length]._id;
      }
      const { members } = within;
      return (direction === 1 ? members[0] : members[members.length - 1])._id;
    },
  };
}

export type AskV2ReviewPinView = ReturnType<typeof useAskV2ReviewPinView>;
